import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import Login from './components/Login';
import WaitingRoom from './components/WaitingRoom';
import PortalSelector from './components/PortalSelector';
import RolesPage from './components/RolesPage';
import Layout from './components/Layout';
import TestSites from './components/TestSites';
import PlaceholderPage from './components/PlaceholderPage';
import ClientDetail from './components/ClientDetail';
import ObjectDetail from './components/ObjectDetail';
import InspectionDetail from './components/InspectionDetail';
import DDLCollection from './components/DDLCollection';
import ArchivedProjectsPage from './components/ArchivedProjectsPage';
import DDLDetail from './components/DDLDetail';
import ObjectsPage from './components/ObjectsPage';
import InspectionsPage from './components/InspectionsPage';
import Dashboard from './components/Dashboard';
import UTTablePage from './components/UTTablePage';
import ShellDeformationPage from './components/ShellDeformationPage';
import AnalysisLayout from './components/AnalysisLayout';
import { AnalysisSidebarProvider } from './contexts/AnalysisSidebarContext';
import { AnalysisActionsProvider } from './contexts/AnalysisActionsContext';
import FloorDeformationPage from './components/FloorDeformationPage';
import EquipmentPage from './components/EquipmentPage';
import CustomersPage from './components/CustomersPage';
import CustomerDetail from './components/CustomerDetail';
import TerraFlowManager from './components/TerraFlowManager';
import TerraFlowProjectDetail from './components/TerraFlowProjectDetail';
import MergepointDashboard from './components/MergepointDashboard';
import { getPermissions } from './utils/permissions';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import AdminNotifications from './components/AdminNotifications';
import VersionUpdateModal from './components/VersionUpdateModal';
import useVersionCheck from './hooks/useVersionCheck';
import './App.css';

function AppRoutes() {
  const { user, userProfile, loading } = useAuth();
  const [authReady, setAuthReady] = useState(false);
  const { currentVersion, latestVersion } = useVersionCheck();

  // NEW: Show environment in browser title
  React.useEffect(() => {
    const env = process.env.REACT_APP_ENV === 'production' ? '' : ' [TEST]';
    document.title = `TIB${env} - Terra Inspectioneering`;
  }, []);

  // Show the branded loading animation while:
  //   • Firebase hasn't finished its first auth-state resolution (!authReady), OR
  //   • auth/profile is actively loading (covers the Google sign-in → profile fetch window)
  // Using `loading` from useAuth() is sufficient — it is already `auth_loading || profileLoading`,
  // so it stays true for the entire sign-in flow.
  // This prevents WaitingRoom from ever flashing for users who already have roles.
  // Rendered alongside every branch below so a stale client always sees
  // the prompt regardless of where they are (login, waiting room, app).
  const versionModal = latestVersion ? (
    <VersionUpdateModal currentVersion={currentVersion} latestVersion={latestVersion} />
  ) : null;

  if (!authReady || loading) {
    return (
      <>
        <LoadingScreen
          isLoading={loading}
          onDone={() => setAuthReady(true)}
        />
        {versionModal}
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Login />
        {versionModal}
      </>
    );
  }

  // User is authenticated but has no roles assigned yet → waiting room
  if (!userProfile || !userProfile.roles || userProfile.roles.length === 0) {
    return (
      <>
        <WaitingRoom />
        {versionModal}
      </>
    );
  }

  const isAdmin = userProfile.roles.includes('admin');
  const perms = getPermissions(userProfile.roles);

  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          {/* ── Portal Selection (landing page after login) ── */}
          <Route path="/" element={<PortalSelector userProfile={userProfile} />} />

          {/* ── Admin — Roles Management (admin-only) ── */}
          <Route
            path="/admin"
            element={isAdmin ? <RolesPage /> : <Navigate to="/" replace />}
          />

          {/* ── UT Table — full-page spreadsheet, opened in new tab ── */}
          <Route
            path="/client/:id/object/:objectId/inspection/:inspectionId/ut-table/:tableId"
            element={<UTTablePage />}
          />

          {/* ── Defo Analysis tabs — wrapped in their own chrome
                (AnalysisLayout) so they get the topbar / sidebar shell
                but without the dashboard-nav behaviour. ── */}
          <Route element={
            <BreadcrumbProvider>
              <AnalysisSidebarProvider>
                <AnalysisActionsProvider>
                  <AnalysisLayout />
                </AnalysisActionsProvider>
              </AnalysisSidebarProvider>
            </BreadcrumbProvider>
          }>
            <Route
              path="/client/:id/object/:objectId/inspection/:inspectionId/shell-deformation"
              element={<ShellDeformationPage />}
            />
            <Route
              path="/client/:id/object/:objectId/inspection/:inspectionId/floor-deformation"
              element={<FloorDeformationPage />}
            />
          </Route>

          {/* ── TIRP — Reporting Portal (all existing routes) ── */}
          <Route element={<BreadcrumbProvider><Layout /></BreadcrumbProvider>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/test-sites" element={<TestSites />} />
            <Route path="/objects" element={<ObjectsPage />} />
            <Route path="/inspections" element={<InspectionsPage />} />
            <Route path="/equipment" element={<EquipmentPage />} />
            <Route path="/ddl-collection" element={<DDLCollection />} />
            <Route path="/ddl-collection/:id" element={<DDLDetail />} />
            <Route path="/archived-projects" element={<ArchivedProjectsPage />} />
            <Route path="/roles" element={<PlaceholderPage title="Roles" />} />
            <Route path="/how-to-use" element={<PlaceholderPage title="How to Use" />} />
            <Route path="/support" element={<PlaceholderPage title="Support" />} />
            <Route path="/client/:id" element={<ClientDetail />} />
            <Route path="/client/:id/object/:objectId" element={<ObjectDetail />} />
            <Route path="/client/:id/object/:objectId/inspection/:inspectionId" element={<InspectionDetail />} />
          </Route>

          {/* ── TerraFlow Manager — its own shell variant ── */}
          <Route element={<BreadcrumbProvider><Layout variant="terraflow" /></BreadcrumbProvider>}>
            <Route
              path="/terraflow"
              element={perms.canAccessTerraFlow ? <TerraFlowManager /> : <Navigate to="/" replace />}
            />
            <Route
              path="/terraflow/project/:projectId"
              element={perms.canAccessTerraFlow ? <TerraFlowProjectDetail /> : <Navigate to="/" replace />}
            />
            <Route
              path="/customers"
              element={perms.canAccessTerraFlow ? <CustomersPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="/customers/:customerId"
              element={perms.canAccessTerraFlow ? <CustomerDetail /> : <Navigate to="/" replace />}
            />
          </Route>

          {/* ── Mergepoint — its own shell variant ── */}
          <Route element={<BreadcrumbProvider><Layout variant="mergepoint" /></BreadcrumbProvider>}>
            <Route path="/mergepoint" element={<MergepointDashboard />} />
          </Route>
        </Routes>
        <AdminNotifications isAdmin={isAdmin} />
      </div>
      {versionModal}
    </BrowserRouter>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
